import React, { useRef, useEffect } from "react";
import { Box, Select, ListSubheader, MenuItem, Link, Radio, ListItemText, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { v4 } from "uuid";
import { MemoizedChip } from "../chip/ChipRenderer.js";
import "../chip/Chips.js";
import "../chip/ListWithChipTooltip.js";
import "../chip/SquaredChipList.js";
import { Icon, IconColor } from "../icons/Icon.js";
import { CustomIcon } from "../icons/types.js";
import "../icons/ExpandMoreIcon.js";
import { LocalizedText } from "../i18n/LocalizedText.js";
import "../input/validation.js";
import "../input/DatePicker.js";
import "../input/DateRangePicker.js";
import { StyledInput } from "../input/StyledInput.js";
import "../input/FormInput.js";
import "../input/StyledFormInputHoc.js";
import "moment-timezone";
import { isNullOrUndefined } from "../../utils/object.js";
import "react-intl";
import "../input/DateSinglePicker.localize.js";
import "../input/hooks.js";
import { useSearch } from "./hooks.js";
import { SelectFooter } from "./MultiSelectFooter.js";
import { useFilterSelectClasses, useDefaultSelectClasses, useGeneralSelectClasses } from "./selectStyle.js";
import { SelectStyle } from "./types.js";
const useSingleSelectClasses = makeStyles()((theme) => ({
  selectClearActionButton: {
    paddingLeft: theme.spacing(0.5),
    color: theme.palette.text.primary,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "none"
    }
  },
  selectClearActionButtonChip: {
    paddingLeft: theme.spacing(1)
  }
}));
const SingleSelect = ({
  options,
  value,
  hasRadio,
  selectStyle,
  emptyValue,
  label,
  onSubmit,
  disabled,
  chipType,
  ChipComponent,
  isSelectedValueChip,
  showResetButton,
  onOpen,
  onClose,
  horizontalPosition,
  startAdornment,
  hasSearch,
  mode,
  onFilterChange,
  searchPlaceholder,
  popoverClassName,
  resetMode = "option",
  fullWidthInput
}) => {
  const { inputValue, visibleOptions, handleSearchChange } = useSearch({
    mode,
    onFilterChange,
    options
  });
  const { classes: selectClasses, cx } = useFilterSelectClasses();
  const { classes: singleSelectClasses } = useSingleSelectClasses();
  const [{ classes }] = React.useState(selectStyle === SelectStyle.Default ? useDefaultSelectClasses() : useFilterSelectClasses());
  const { classes: generalSelectClasses } = useGeneralSelectClasses();
  const singleselectId = useRef(v4());
  const defaultValue = "";
  const [selectedValue, setSelectedValue] = React.useState(defaultValue);
  const [selectOpen, setSelectOpen] = React.useState(false);
  const arrow = selectOpen ? CustomIcon.ArrowUp : CustomIcon.ArrowDown;
  const openStyle = selectOpen ? generalSelectClasses.selectButtonOpen : "";
  const selectArrowIcon = /* @__PURE__ */ React.createElement(Box, { component: "span", className: `selectIcon ${arrow} ${generalSelectClasses.selectButtonEndIcon} ${openStyle}` });
  useEffect(() => {
    setSelectedValue(!isNullOrUndefined(value) ? value : defaultValue);
  }, [value]);
  const handleChange = (event) => {
    const targetValue = event.target.value;
    if (!isNullOrUndefined(targetValue)) {
      setSelectedValue(targetValue);
      onSubmit([targetValue]);
      return;
    }
    setSelectedValue(null);
    onSubmit(null);
  };
  const handleClose = () => {
    setSelectOpen(false);
    if (onClose) {
      onClose();
    }
  };
  const handleOpen = () => {
    setSelectOpen(true);
    if (onOpen) {
      onOpen();
    }
  };
  const convertSelectedValueToString = (value2) => {
    return Array.isArray(value2) && value2.length > 0 ? value2[0] : value2;
  };
  const getRenderValue = (value2) => {
    const selectedOption = options?.find((o) => o.value === convertSelectedValueToString(value2));
    const selectedValue2 = selectedOption?.label;
    const selectedAndChip = isSelectedValueChip && selectedValue2;
    const notSelectedAndChip = isSelectedValueChip && !selectedValue2;
    let wrapperClass = generalSelectClasses.selectedValueEllipsis;
    if (selectedAndChip) {
      wrapperClass = generalSelectClasses.selectedValueWithChip;
    }
    if (notSelectedAndChip) {
      wrapperClass = generalSelectClasses.notSelectedValueWithChip;
    }
    return /* @__PURE__ */ React.createElement(Box, { className: wrapperClass }, label ? /* @__PURE__ */ React.createElement(Typography, { variant: "body1", className: classes.selectLabel, component: "span" }, /* @__PURE__ */ React.createElement(LocalizedText, { label })) : /* @__PURE__ */ React.createElement(React.Fragment, null), selectedAndChip ? chipType ? /* @__PURE__ */ React.createElement(MemoizedChip, { chipName: chipType, value: value2, label }) : /* @__PURE__ */ React.createElement(ChipComponent, { value: value2 }) : /* @__PURE__ */ React.createElement(Typography, { variant: "body1", component: "span" }, /* @__PURE__ */ React.createElement(LocalizedText, { label: selectedValue2 ? selectedValue2 : emptyValue })));
  };
  return /* @__PURE__ */ React.createElement(
    Select,
    {
      variant: "standard",
      disabled,
      "data-testid": "singleSelect",
      inputProps: { "data-testid": "singleSelectInput" },
      displayEmpty: true,
      value: selectedValue,
      onChange: handleChange,
      onClose: handleClose,
      onOpen: handleOpen,
      renderValue: getRenderValue,
      MenuProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: horizontalPosition
        },
        transformOrigin: {
          vertical: "top",
          horizontal: horizontalPosition
        },
        className: cx(selectClasses.selectPopover, popoverClassName)
      },
      IconComponent: () => selectArrowIcon,
      className: cx(classes.selectInput, {
        [selectClasses.selectInputFullWidth]: fullWidthInput
      }),
      disableUnderline: true,
      startAdornment
    },
    hasSearch ? /* @__PURE__ */ React.createElement(ListSubheader, { disableGutters: true }, /* @__PURE__ */ React.createElement(
      StyledInput,
      {
        value: inputValue,
        className: selectClasses.selectSearch,
        onKeyDown: (e) => e.stopPropagation(),
        onChange: handleSearchChange,
        placeholder: searchPlaceholder
      }
    )) : null,
    /* @__PURE__ */ React.createElement(MenuItem, { value: "", className: generalSelectClasses.selectItemHidden }),
    showResetButton && resetMode === "option" && !inputValue ? /* @__PURE__ */ React.createElement(MenuItem, { value: null, className: generalSelectClasses.selectWithConfirmMenuItem }, /* @__PURE__ */ React.createElement(
      Link,
      {
        className: `${singleSelectClasses.selectClearActionButton} ${chipType ? singleSelectClasses.selectClearActionButtonChip : ""}`
      },
      /* @__PURE__ */ React.createElement(LocalizedText, { label: emptyValue })
    )) : null,
    visibleOptions?.map((option, index) => /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        className: generalSelectClasses.selectWithConfirmMenuItem,
        value: option.value,
        key: `singleselect-${singleselectId}-${index}`
      },
      hasRadio && /* @__PURE__ */ React.createElement(Radio, { color: "primary", checked: option.value === convertSelectedValueToString(selectedValue) }),
      /* @__PURE__ */ React.createElement(
        ListItemText,
        {
          className: generalSelectClasses.listItemText,
          disableTypography: true,
          primary: chipType ? /* @__PURE__ */ React.createElement(MemoizedChip, { chipName: chipType, value: option.value, cursorPointer: true, label: option.label }) : ChipComponent ? /* @__PURE__ */ React.createElement(ChipComponent, { value: option.value }) : /* @__PURE__ */ React.createElement(Typography, { variant: "button", className: generalSelectClasses.listItemTextTypography }, /* @__PURE__ */ React.createElement(LocalizedText, { label: option.label }))
        }
      ),
      /* @__PURE__ */ React.createElement(Icon, { icon: CustomIcon.Confirm, color: IconColor.Primary, className: generalSelectClasses.selectWithConfirmIcon })
    )),
    showResetButton && resetMode === "button" ? /* @__PURE__ */ React.createElement(SelectFooter, { showResetButton, onClear: () => onSubmit("") }) : null
  );
};
export {
  SingleSelect
};
//# sourceMappingURL=SingleSelect.js.map
